import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BackorderRow,
  CreateBackorderBatchInput,
  CreateBackorderInput,
} from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import {
  backorderApi,
  type BackorderPage,
  type BackorderQuery,
  type FulfilItem,
} from '../api/backorder.api.js';

export const backorderKeys = {
  mine: (query: BackorderQuery) => ['backorders', 'mine', query] as const,
  queue: (query: BackorderQuery) => ['backorders', 'queue', query] as const,
  ready: (query: BackorderQuery) => ['backorders', 'ready', query] as const,
  readyCount: () => ['backorders', 'ready', 'count'] as const,
  pendingCount: () => ['backorders', 'pending', 'count'] as const,
  one: (id: string) => ['backorders', 'one', id] as const,
};

export const useMyBackorders = (
  query: BackorderQuery = {},
): UseQueryResult<BackorderPage, ApiClientError> =>
  useQuery<BackorderPage, ApiClientError>({
    queryKey: backorderKeys.mine(query),
    queryFn: () => backorderApi.listMine(query),
    retry: false,
  });

export const useBackorderQueue = (
  query: BackorderQuery = {},
  enabled = true,
): UseQueryResult<BackorderPage, ApiClientError> =>
  useQuery<BackorderPage, ApiClientError>({
    queryKey: backorderKeys.queue(query),
    queryFn: () => backorderApi.listQueue(query),
    enabled,
    retry: false,
  });

export const useReadyBackorders = (
  query: BackorderQuery = {},
  enabled = true,
): UseQueryResult<BackorderPage, ApiClientError> =>
  useQuery<BackorderPage, ApiClientError>({
    queryKey: backorderKeys.ready(query),
    queryFn: () => backorderApi.listReady(query),
    enabled,
    retry: false,
  });

/**
 * Yon menyudagi son.
 *
 * `enabled` MAJBURIY emas, lekin chaqiruvchi uni ruxsatga bog'laydi:
 * `backorder.accept` siz odam uchun so'rov 403 qaytarardi va menyuda
 * sababsiz xato chiqardi.
 */
export const useReadyBackorderCount = (enabled = true): UseQueryResult<number, ApiClientError> =>
  useQuery<number, ApiClientError>({
    queryKey: backorderKeys.readyCount(),
    queryFn: () => backorderApi.readyCount(),
    enabled,
    retry: false,
  });

/** Yangi bildirgi skladchiga sahifani yangilamasdan yetib borishi uchun. */
export const PENDING_REFETCH_MS = 30_000;

/**
 * Ko'rib chiqilmagan bildirgilar soni — skladchining QIZIL belgisi.
 *
 * Davriy yangilanadi: yangi ariza boshqa odamning harakatidan keladi va
 * skladchi uni sahifani qayta yuklamasdan ko'rishi kerak. `enabled`
 * ruxsatga bog'lanadi — `backorder.accept` siz odamga bu son kerak emas.
 */
export const usePendingBackorderCount = (
  enabled = true,
): UseQueryResult<number, ApiClientError> =>
  useQuery<number, ApiClientError>({
    queryKey: backorderKeys.pendingCount(),
    queryFn: () => backorderApi.pendingCount(),
    enabled,
    refetchInterval: PENDING_REFETCH_MS,
    retry: false,
  });

/**
 * Bildirgidan keyin YANGILANADIGAN ro'yxatlar.
 *
 * Talabnomalar ham shu yerda: biriktirish talabnoma TUG'DIRADI va u
 * darrov «mening talabnomalarim» da ko'rinishi kerak. Yangilanmasa,
 * buyurtmachi xabar olgan bo'lsa-da, ro'yxatida hech nima topmasdi.
 */
const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>): void => {
  void queryClient.invalidateQueries({ queryKey: ['backorders'] });
  void queryClient.invalidateQueries({ queryKey: ['requisitions'] });
};

export const useCreateBackorder = () => {
  const queryClient = useQueryClient();

  return useMutation<BackorderRow, ApiClientError, CreateBackorderInput>({
    mutationFn: (input) => backorderApi.create(input),
    onSuccess: () => invalidateAll(queryClient),
  });
};

export const useCreateBackorderBatch = () => {
  const queryClient = useQueryClient();

  return useMutation<BackorderRow[], ApiClientError, CreateBackorderBatchInput>({
    mutationFn: (input) => backorderApi.createBatch(input),
    onSuccess: () => invalidateAll(queryClient),
  });
};

export type BackorderAction =
  | { id: string; action: 'accept'; nomenclatureId: string | null }
  | { id: string; action: 'reject'; reason: string }
  | { id: string; action: 'cancel' };

/**
 * Uch amal BITTA mutatsiyada.
 *
 * Ular bir xil javobni qaytaradi va bir xil ro'yxatlarni eskirtiradi;
 * to'rtta alohida hook yozilsa, `onSuccess` mantig'i to'rt marta
 * takrorlanardi va bir kun ulardan biri talabnomalarni yangilashni
 * unutardi (`useReviewWriteOff` bilan ayni qaror).
 */
export const useBackorderAction = () => {
  const queryClient = useQueryClient();

  return useMutation<BackorderRow, ApiClientError, BackorderAction>({
    mutationFn: (input) => {
      switch (input.action) {
        case 'accept':
          return backorderApi.accept(input.id, input.nomenclatureId);
        case 'reject':
          return backorderApi.reject(input.id, input.reason);
        case 'cancel':
          return backorderApi.cancel(input.id);
      }
    },
    onSuccess: () => invalidateAll(queryClient),
  });
};

/**
 * BIRIKTIRISH — bitta bildirgi ham, bir nechtasi ham.
 *
 * `useBackorderAction` dan AJRATILDI: u bitta qatorni qaytaradi, bu esa
 * ro'yxatni, va chaqiruvchiga tug'ilgan talabnomaning raqami kerak —
 * biriktirgandan keyin ekran o'sha hujjatni ochadi.
 */
export const useFulfilBackorders = () => {
  const queryClient = useQueryClient();

  return useMutation<BackorderRow[], ApiClientError, readonly FulfilItem[]>({
    mutationFn: (items) => backorderApi.fulfilMany(items),
    onSuccess: () => invalidateAll(queryClient),
  });
};
